import { shallowEqual } from 'react-redux';
import { TAutocomplete, TId, TOption } from './types';
import { addBoundary } from '../../../../utils/number';
import EventEmitter from '../../../../utils/util/eventEmitter';

type TFocusSuscriber = (isFocused: boolean) => unknown;
type TFocusUpdater = (
  currentFocusedIndex: number,
  options: TStoredOption[],
) => number;

type TListUpdater = (currentList: TStoredOption[]) => TOption[];

export type TStoredOption = TOption & { id: number; isHidden?: boolean };

export default class AutocompleteStore extends EventEmitter<{
  focusChange: number;
  isLoading: boolean;
  listChange: TStoredOption[];
  showValueChange: TOption | string | undefined;
  valueChange: TOption | undefined;
}> {
  maxId = 0;

  /**
   * Value
   */
  showValue: TOption | string | undefined = undefined;

  setShowValue(newShowValue?: TOption | string) {
    this.showValue = newShowValue;
    this.emit('showValueChange', newShowValue);
  }

  value: TId | undefined;

  updateProps(
    props: Pick<
      TAutocomplete,
      'defaultOption' | 'initialOptions' | 'value' | 'onLoadData'
    >,
  ) {
    const hasChangedLoadData =
      props.onLoadData && props.onLoadData !== this.props.onLoadData;
    this.props = props;

    if (props.value === '') {
      this.setValue(props.value);
      this.setShowValue(props.value);
    }

    const applyConfig = () => {
      if (props.defaultOption?.value === (props.value ?? this.value)) {
        setTimeout(() => {
          this.setValue(props.defaultOption?.value);
          this.setShowValue(props.defaultOption?.label as string);
        }, 0);
      }
    };

    if (hasChangedLoadData) {
      this.props.onLoadData = props.onLoadData;
      setTimeout(() => {
        void this.loadOptions('').then(() => applyConfig());
      }, 0);
    } else applyConfig();
  }
  confirmChangeValue: boolean;
  avoidBlur: boolean = false;
  toggleBlur = (value?: boolean) => {
    if (value !== undefined) {
      this.avoidBlur = value;
    } else {
      this.avoidBlur = !this.avoidBlur;
    }
  };
  constructor(
    private props: Pick<
      TAutocomplete,
      | 'defaultOption'
      | 'initialOptions'
      | 'value'
      | 'onLoadData'
      | 'confirmChangeValue'
    >,
  ) {
    super();

    this.options = [];
    this.confirmChangeValue = !!props.confirmChangeValue;
    if (props?.initialOptions)
      this.options = props.initialOptions.map((current) => ({
        ...current,
        id: this.maxId++,
      }));

    this.value = props.value;

    if (props?.defaultOption) {
      if (
        !this.options.find(
          (current) => current.value === props.defaultOption?.value,
        )
      ) {
        this.options.push({ ...props.defaultOption, id: this.maxId++ });
        if (props.value === undefined) {
          this.value = props.defaultOption.value;
        }
      }
    }

    this.showValue = this.lastShowValue = this.options.find(
      (current) => current.value === this.value,
    );
    this.focusedOptionIndex = this.options.findIndex(
      (current) => current.value === this.value,
    );
  }

  lastShowValue: TStoredOption | undefined = undefined;

  setValue = (newValue?: TId) => {
    if (newValue !== this.value) {
      const newValueIndex = this.options.findIndex(
        (current) => current.value === newValue,
      );

      if (newValueIndex > -1) {
        this.focusedOptionIndex = newValueIndex;
        this.value = newValue;
        this.emit('valueChange', this.options[this.focusedOptionIndex]);
        this.setShowValue(this.options[this.focusedOptionIndex]);
        this.lastShowValue = this.options[this.focusedOptionIndex];
      } else {
        this.setShowValue(this.lastShowValue);
      }
    } else {
      this.setShowValue(this.lastShowValue);
    }
  };

  resetValue() {
    this.setShowValue(this.lastShowValue);
    this.updateFocused((_, options) =>
      options.findIndex((current) => current.value === this.value),
    );
  }

  /**
   * Focus
   */
  protected focusedOptionIndex = 0;

  focusSuscribers: Record<TId, TFocusSuscriber[]> = {};

  /**
   * Permite saber si una opción está en foco
   */
  isFocused(id: number) {
    return (
      this.options[this.focusedOptionIndex]?.id === id ||
      (!this.options[this.focusedOptionIndex] && this.options[0]?.id === id)
    );
  }

  loadDebounceTimeout = 0;

  isLoading = false;

  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
    this.emit('isLoading', isLoading);
  }

  loadOptions = (searchString = '', debounce = 200) => {
    return new Promise<TOption[] | null>((resolve) => {
      if (!this.props.onLoadData) {
        resolve(null);
        return;
      }

      this.setIsLoading(true);
      clearTimeout(this.loadDebounceTimeout);
      this.loadDebounceTimeout = setTimeout(
        () => {
          if (!this.props.onLoadData) {
            resolve(null);
            return;
          }
          const result = this.props.onLoadData(searchString);

          if (result instanceof Promise) {
            void result.then((newOptions) => {
              this.updateList(() => {
                return newOptions;
              });
              resolve(newOptions);
            });
          } else {
            this.updateList(() => {
              return result;
            });
            resolve(result);
          }
          this.setIsLoading(false);
          this.loadDebounceTimeout = -1;
        },
        this.isLoading ? debounce : 0,
      ) as unknown as number;
    });
  };

  /**
   * Permite suscribirse al estado de enFoco de una opción
   */
  onOptionChangeFocus(id: number, suscriber: TFocusSuscriber) {
    if (!this.focusSuscribers[id]) this.focusSuscribers[id] = [];

    this.focusSuscribers[id].push(suscriber);

    return () => {
      this.focusSuscribers[id] = this.focusSuscribers[id].filter(
        (current) => current !== suscriber,
      );
    };
  }

  /**
   * Permite actualizar el elemento en foco, se debe pasar un callback
   */
  updateFocused(updater: TFocusUpdater, updateShowValue = true) {
    const newIndex = addBoundary(
      updater(this.focusedOptionIndex, this.options),
      0,
      this.options.length - 1,
    );

    const focusedOption = this.options[this.focusedOptionIndex];
    const newFocusedOption = this.options[newIndex];
    this.focusedOptionIndex = newIndex;

    if (focusedOption && newFocusedOption) {
      this.focusSuscribers[focusedOption.id]?.forEach((current) =>
        current(false),
      );
    }
    if (newFocusedOption) {
      this.focusSuscribers[newFocusedOption.id]?.forEach((current) =>
        current(true),
      );
      if (updateShowValue) this.setShowValue(newFocusedOption);
      this.emit('focusChange', newIndex);
    }
  }

  /**
   * List of options
   */
  options: TStoredOption[] = [];

  getByValue(value: TId) {
    return this.options.find((current) => current.value === value);
  }

  getFocusedOption() {
    return this.options[this.focusedOptionIndex];
  }

  shoutListChange() {
    this.emit(
      'listChange',
      this.options.filter((currentOption) => !currentOption.isHidden),
    );
  }

  optionsIds: Record<string, number> = {};

  updateList(callback: TListUpdater) {
    const previousOptions = this.options;

    this.options = callback([...this.options]).map((current) => {
      let currentId = this.optionsIds[`${current.label}___${current.value}`];
      if (!currentId) {
        currentId = this.maxId++;
        this.optionsIds[`${current.label}___${current.value}`] = currentId;
      }

      return { ...current, id: currentId };
    });

    this.updateFocused(() => {
      const newFocused = this.options.findIndex(
        (current) =>
          current.value === previousOptions[this.focusedOptionIndex]?.value,
      );
      return newFocused;
    }, false);

    if (!shallowEqual(previousOptions, this.options)) {
      this.shoutListChange();
    }
  }
}
